import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Award, ImageOff, Plus, Scissors, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import TestimonialFrage from '@/components/TestimonialFrage';
import TestimonialZuschnitt from '@/components/TestimonialZuschnitt';
import { ergebnisText, fehlerText } from '@/lib/testimonialClient';

/* Testimonial-Karte der Kundenakte (Coach-Cockpit W1.7) — im CRM und auf /coach, je nach `client`
   (lib/testimonialClient.js). Einwilligung festhalten (TestimonialFrage), Kennzahlen wählen (Vorschlag aus Check-up oder
   Erfolgsreport), Fotos ohne Gesicht zuschneiden, für die Bibliothek freigeben, Widerruf vermerken. Rechte prüft der Server. */

const KENNZAHLEN = [
  ['gewicht_kg', 'Gewicht (kg)'], ['fett_prozent', 'Körperfett (%)'], ['muskelmasse_kg', 'Muskelmasse (kg)'], ['viszeral', 'Viszerales Fett'],
  ['taille', 'Taille (cm)'], ['huefte', 'Hüfte (cm)'], ['brust', 'Brust (cm)'], ['arm', 'Arm (cm)'], ['oberschenkel', 'Oberschenkel (cm)'],
  ['frei', 'Eigene Kennzahl'],
].map(([value, label]) => ({ value, label }));
const EINHEITEN = ['kg', 'cm', '%', 'Wdh.', 'Min.', 'Sek.', 'km', 'Tage', ''].map((e) => ({ value: e || 'ohne', label: e || 'ohne Einheit' }));
const STATUS = { entwurf: 'Entwurf', frei: 'in der Bibliothek', zurueckgezogen: 'zurückgezogen' };
const ANSICHT = { vorne: 'vorne', seite: 'seitlich', hinten: 'hinten' };
const tag = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '');

/* `laden` darf bei jedem Zeichnen neu sein — geladen wird nur, wenn sich `schluessel` ändert */
export function Bild({ laden, schluessel, alt }) {
  const [url, setUrl] = useState(null);
  const [weg, setWeg] = useState(false);
  useEffect(() => {
    let u = null;
    let aktiv = true;
    laden().then((r) => { if (aktiv) { u = URL.createObjectURL(r.data); setUrl(u); } }).catch(() => setWeg(true));
    return () => { aktiv = false; if (u) URL.revokeObjectURL(u); };
  }, [schluessel]); // eslint-disable-line react-hooks/exhaustive-deps
  if (weg) return <div className="flex aspect-square items-center justify-center rounded-md bg-muted"><ImageOff className="h-5 w-5 text-muted-foreground" /></div>;
  if (!url) return <Skeleton className="aspect-square rounded-md" />;
  return <img src={url} alt={alt} className="aspect-square w-full rounded-md object-cover" />;
}

function ZahlenEditor({ zahlen, setZahlen }) {
  const setze = (i, feld, wert) => setZahlen(zahlen.map((z, j) => (j === i ? { ...z, [feld]: wert } : z)));
  return (
    <div className="space-y-2">
      {zahlen.map((z, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <NativeSelect className="h-11 w-48" value={z.key} onChange={(v) => setze(i, 'key', v)} options={KENNZAHLEN} aria-label="Kennzahl" />
          {z.key === 'frei' && <Input className="h-11 w-40" placeholder="Beschriftung" value={z.label || ''} maxLength={40} onChange={(e) => setze(i, 'label', e.target.value)} />}
          <Input className="h-11 w-28" inputMode="decimal" placeholder="z. B. -4,2" value={z.wert ?? ''} onChange={(e) => setze(i, 'wert', e.target.value)} aria-label="Veränderung" />
          {z.key === 'frei' && <NativeSelect className="h-11 w-32" value={z.einheit || 'ohne'} onChange={(v) => setze(i, 'einheit', v === 'ohne' ? '' : v)} options={EINHEITEN} aria-label="Einheit" />}
          <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => setZahlen(zahlen.filter((_, j) => j !== i))} aria-label="Kennzahl entfernen"><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      {zahlen.length < 6 && <Button variant="outline" size="sm" className="min-h-11" onClick={() => setZahlen([...zahlen, { key: 'gewicht_kg', wert: '' }])}><Plus className="mr-1 h-4 w-4" /> Kennzahl</Button>}
    </div>
  );
}

function Neu({ client, quellen, onFertig }) {
  const [quelle, setQuelle] = useState('');
  const [zahlen, setZahlen] = useState([{ key: 'gewicht_kg', wert: '' }]);
  const [zeitraum, setZeitraum] = useState('4 Wochen');
  const [laeuft, setLaeuft] = useState(false);
  const optionen = quellen.map((q) => ({ value: `${q.art}|${q.id}`, label: `${q.art === 'checkup' ? 'Check-up' : 'Erfolgsreport'} ${tag(q.tag)}` }));
  const uebernehmen = async () => {
    const [art, id] = quelle.split('|');
    try {
      const r = await client.vorschlag(art, id);
      if (r.data.zahlen.length) setZahlen(r.data.zahlen.map((z) => ({ ...z, wert: String(z.wert).replace('.', ',') })));
      else toast.message('In dieser Quelle gibt es keine Veränderung zum Übernehmen');
      if (r.data.zeitraum) setZeitraum(r.data.zeitraum);
    } catch (e) { toast.error(fehlerText(e, 'Vorschlag nicht geladen')); }
  };
  const anlegen = async () => {
    setLaeuft(true);
    try {
      const [art, id] = quelle ? quelle.split('|') : [];
      await client.anlegen({ zahlen: zahlen.map((z) => ({ ...z, wert: Number(String(z.wert).replace(',', '.')) })), zeitraum: zeitraum || null,
        quelle: art ? { art, id } : null });
      toast.success('Testimonial angelegt'); onFertig();
    } catch (e) { toast.error(fehlerText(e, 'Nicht angelegt')); }
    setLaeuft(false);
  };
  return (
    <div className="space-y-3 rounded-lg border border-border/60 p-3" data-testid="testimonial-neu">
      <p className="text-sm font-semibold">Neues Testimonial — nur Zahlen, nie ein Name</p>
      {optionen.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <NativeSelect className="h-11 w-56" value={quelle} onChange={setQuelle} options={optionen} placeholder="Quelle (optional)" aria-label="Quelle" />
          <Button variant="outline" className="min-h-11" disabled={!quelle} onClick={uebernehmen}>Zahlen übernehmen</Button>
        </div>
      )}
      <ZahlenEditor zahlen={zahlen} setZahlen={setZahlen} />
      <div className="flex flex-wrap items-center gap-2">
        <Input className="h-11 w-40" value={zeitraum} onChange={(e) => setZeitraum(e.target.value)} placeholder="4 Wochen" aria-label="Zeitraum" maxLength={20} />
        <Button className="min-h-11" onClick={anlegen} disabled={laeuft || !zahlen.length}>Anlegen</Button>
      </div>
      <p className="text-xs text-muted-foreground">Werte als Veränderung eingeben (minus = weniger). Zeitraum z. B. „4 Wochen“ oder „3 Monate“.</p>
    </div>
  );
}

function Eintrag({ t, client, mitFotos, fotos, onNeu }) {
  const [waehle, setWaehle] = useState(false);
  const [foto, setFoto] = useState(null);
  const aktion = async (fn, ok, rest) => {
    try { await fn(); if (ok) toast.success(ok); onNeu(); } catch (e) { toast.error(fehlerText(e, rest)); }
  };
  const vorlageLaden = useCallback(() => client.vorlage(foto), [client, foto]);
  const offen = t.status !== 'zurueckgezogen';
  return (
    <div className="space-y-2 rounded-lg border border-border/60 p-3" data-testid={`testimonial-${t.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{ergebnisText(t.zahlen, t.zeitraum)}</span>
        <Badge variant={t.status === 'frei' ? 'default' : 'outline'} className="text-[10px]">{STATUS[t.status] || t.status}</Badge>
        {t.contentplan_idee && <Badge variant="outline" className="text-[10px]">Idee im Contentplan</Badge>}
      </div>
      {t.medien.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {t.medien.map((m) => (
            <figure key={m.id} className="space-y-1">
              <Bild laden={() => client.medium(t.id, m.id, true)} schluessel={m.id} alt="Zuschnitt ohne Gesicht" />
              <figcaption className="text-[11px] text-muted-foreground">{m.kunde_freigabe_am ? `Kunde frei ${tag(m.kunde_freigabe_am)}` : 'wartet auf Kunde'}</figcaption>
              {offen && <button type="button" className="text-[11px] underline" onClick={() => aktion(() => client.mediumWeg(t.id, m.id), 'Zuschnitt entfernt', 'Nicht entfernt')}>entfernen</button>}
            </figure>
          ))}
        </div>
      )}
      {offen && (
        <div className="flex flex-wrap gap-2">
          {mitFotos && fotos.length > 0 && <Button variant="outline" size="sm" className="min-h-11" onClick={() => { setWaehle((v) => !v); setFoto(null); }}><Scissors className="mr-1 h-4 w-4" /> Foto zuschneiden</Button>}
          {t.status === 'entwurf' && <Button size="sm" className="min-h-11" onClick={() => aktion(() => client.freigeben(t.id), 'In der Bibliothek', 'Nicht freigegeben')}>Für die Bibliothek freigeben</Button>}
          <Button variant="ghost" size="sm" className="min-h-11" onClick={() => aktion(() => client.zurueckziehen(t.id), 'Zurückgezogen', 'Nicht zurückgezogen')}>Zurückziehen</Button>
        </div>
      )}
      {waehle && !foto && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Fortschrittsfoto wählen:</p>
          <div className="flex flex-wrap gap-2">
            {fotos.map((f) => (
              <Button key={f.id} variant="outline" size="sm" className="min-h-11" onClick={() => setFoto(f.id)}>{tag(f.datum)} · {ANSICHT[f.ansicht] || f.ansicht}</Button>
            ))}
          </div>
        </div>
      )}
      {foto && (
        <TestimonialZuschnitt laden={vorlageLaden} onAbbrechen={() => { setFoto(null); setWaehle(false); }}
          onSpeichern={async (zuschnitt, kopf) => {
            try {
              await client.zuschnitt(t.id, { foto_id: foto, zuschnitt, kopf_ausserhalb: kopf });
              toast.success('Zuschnitt gespeichert — das Mitglied gibt ihn in der App frei'); setFoto(null); setWaehle(false); onNeu();
            } catch (e) { toast.error(fehlerText(e, 'Zuschnitt nicht gespeichert')); }
          }} />
      )}
    </div>
  );
}

export default function TestimonialKarte({ client, testId = 'testimonial-karte' }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [neu, setNeu] = useState(false);
  const lade = useCallback(() => {
    client.stand().then((r) => { setD(r.data); setErr(''); }).catch((e) => setErr(fehlerText(e, 'Testimonial nicht geladen')));
  }, [client]);
  useEffect(() => { lade(); }, [lade]);

  if (err) return null;            // ohne Recht (403) bleibt die Karte weg
  if (!d) return <Skeleton className="h-24" />;
  const e = d.einwilligung || {};
  const fotos = (d.fotos?.tage || []).flatMap((t) => Object.entries(t.fotos || {}).map(([ansicht, f]) => ({ id: f.id, datum: t.datum, ansicht })));
  const wahl = async (umfang, version) => {
    try {
      const r = await client.einwilligen(umfang, version);
      toast.success(umfang === 'nein' ? 'Antwort „Nein“ festgehalten' : 'Einwilligung festgehalten');
      if (r.data?.zuschnitte_geloescht) toast.message(`${r.data.zuschnitte_geloescht} Zuschnitt(e) gelöscht`);
      lade();
    } catch (x) { toast.error(fehlerText(x, 'Nicht gespeichert')); }
  };
  const widerruf = async () => {
    if (!window.confirm('Widerruf vermerken? Alle Testimonials dieses Mitglieds verschwinden sofort aus der Bibliothek, Zuschnitte werden gelöscht.')) return;
    try { await client.widerrufen(e.id); toast.success('Widerruf vermerkt — Material entfernt'); lade(); }
    catch (x) { toast.error(fehlerText(x, 'Widerruf nicht vermerkt')); }
  };
  return (
    <Card className="border-border/60" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base"><Award className="h-4 w-4 text-marke" /> Testimonial
          {e.erteilt && <Badge variant="outline" className="text-[10px]">{e.mit_fotos ? 'Zahlen + Fotos ohne Gesicht' : 'nur Zahlen'} · seit {tag(e.erteilt_am)}</Badge>}
          {e.status === 'widerrufen' && <Badge variant="outline" className="text-[10px]">widerrufen {tag(e.widerrufen_am)}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TestimonialFrage aktiv={d.aktiv} text={d.einwilligung_text} stand={e} onWahl={wahl} />
        {e.widerrufbar && <Button variant="ghost" size="sm" className="min-h-11 text-destructive" onClick={widerruf}>Widerruf vermerken</Button>}
        {d.aktiv && e.erteilt && (
          <>
            {d.testimonials.map((t) => <Eintrag key={t.id} t={t} client={client} mitFotos={e.mit_fotos} fotos={fotos} onNeu={lade} />)}
            {e.mit_fotos && !d.fotos?.aktiv && <p className="text-xs text-muted-foreground">Für Zuschnitte braucht es Fortschrittsfotos (Schalter und Einwilligung des Mitglieds).</p>}
            {neu ? <Neu client={client} quellen={d.quellen || []} onFertig={() => { setNeu(false); lade(); }} />
              : <Button variant="outline" className="min-h-11" onClick={() => setNeu(true)}><Plus className="mr-1 h-4 w-4" /> Testimonial anlegen</Button>}
          </>
        )}
      </CardContent>
    </Card>
  );
}
